import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { CustomsClearanceEndpointService } from '../clearance.service';
import { ClearanceStatus } from '../models/clearance-status.enum';
import { CustomsClearanceHeader } from '../models/customs-clearance-header.model';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { SessionStorageService } from '../../services/session-storage.service';

interface StatusBucket {
    label: string;
    value: number;
    total: number;
}

@Component({
    selector: 'app-clearance-overview',
    standalone: true,
    templateUrl: './clearance-overview.component.html',
    styleUrls: ['./clearance-overview.component.css'],
    imports: [
        CommonModule,
        RouterModule,
        ButtonModule,
        ProgressBarModule,
        TableModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [MessageService]
})
export class ClearanceOverviewComponent implements OnInit {
    clearances: CustomsClearanceHeader[] = [];
    isLoading = true;
    canViewClearance = false;
    canCreateClearance = false;
    totalRecords = 0;

    constructor(
        private clearanceService: CustomsClearanceEndpointService,
        private messageService: MessageService,
        private permissionService: PermissionService,
        private router: Router,
        private sessionStorageService: SessionStorageService
    ) {}

    ngOnInit(): void {
        this.canViewClearance = this.permissionService.hasPermission(app_permissions.clearance.records.view);
        this.canCreateClearance = this.permissionService.hasPermission(app_permissions.clearance.records.create);
        this.loadDashboard();
    }

    loadDashboard(): void {
        if (!this.canViewClearance) {
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        this.clearanceService.getAllCustomsClearanceHeadersByCompany(
            this.sessionStorageService.getCompany()?.id ?? 0,
            0,
            50,
            'clearanceHeaderID',
            'DESC'
        ).subscribe({
            next: page => {
                this.clearances = page.content || [];
                this.totalRecords = page.totalElements || this.clearances.length;
                this.isLoading = false;
            },
            error: error => {
                const detail = error?.error?.message || 'Could not load clearance dashboard.';
                this.messageService.add({ severity: 'error', summary: 'Dashboard unavailable', detail });
                this.clearances = [];
                this.totalRecords = 0;
                this.isLoading = false;
            }
        });
    }

    addClearance(): void {
        this.router.navigate(['/clearance/add']);
    }

    openList(): void {
        this.router.navigate(['/clearance/list']);
    }

    openWorkspace(clearance: CustomsClearanceHeader): void {
        this.router.navigate(['/clearance/view', clearance.clearanceHeaderID]);
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Total Jobs', value: this.totalRecords, hint: 'Company clearance records', tone: 'info' },
            { label: 'Pre-Clearance Risk', value: this.preClearanceRisk.length, hint: 'Late or due within 48h', tone: this.preClearanceRisk.length ? 'danger' : 'success' },
            { label: 'Document Actions', value: this.documentActionJobs.length, hint: 'Review or correction needed', tone: this.documentActionJobs.length ? 'warn' : 'success' },
            { label: 'Customs Filing', value: this.filingJobs.length, hint: 'Submitted, filing, or inspection', tone: 'info' },
            { label: 'Release Handoff', value: this.releaseJobs.length, hint: 'Released, delivery, or complete', tone: 'success' }
        ];
    }

    get statusBuckets(): StatusBucket[] {
        const total = Math.max(this.clearances.length, 1);
        return [
            { label: 'Intake', value: this.countByStatuses([ClearanceStatus.DRAFT, ClearanceStatus.REQUEST_SUBMITTED, ClearanceStatus.REQUEST_ACCEPTED]), total },
            { label: 'Documents', value: this.countByStatuses([ClearanceStatus.DOCS_UPLOADED, ClearanceStatus.DOCS_REVIEW_IN_PROGRESS, ClearanceStatus.DOCS_REVIEW_REJECTED]), total },
            { label: 'Filing', value: this.filingJobs.length, total },
            { label: 'Release', value: this.releaseJobs.length, total },
            { label: 'Exceptions', value: this.exceptionJobs.length, total }
        ];
    }

    get preClearanceRisk(): CustomsClearanceHeader[] {
        return this.clearances
            .filter(clearance => this.isPreClearanceLate(clearance) || this.isPreClearanceDueSoon(clearance))
            .slice(0, 6);
    }

    get documentActionJobs(): CustomsClearanceHeader[] {
        return this.clearances
            .filter(clearance => [
                ClearanceStatus.DOCS_UPLOADED,
                ClearanceStatus.DOCS_REVIEW_IN_PROGRESS,
                ClearanceStatus.DOCS_REVIEW_REJECTED
            ].includes(clearance.status as ClearanceStatus))
            .slice(0, 6);
    }

    get filingJobs(): CustomsClearanceHeader[] {
        return this.clearances.filter(clearance => [
            ClearanceStatus.CUSTOMS_FILING_IN_PROGRESS,
            ClearanceStatus.CUSTOMS_FILING_COMPLETED,
            ClearanceStatus.CUSTOMS_INSPECTION,
            ClearanceStatus.PORT_CHARGES_PENDING,
            ClearanceStatus.PORT_CHARGES_PAID,
            ClearanceStatus.DELIVERY_ORDER_RECEIVED
        ].includes(clearance.status as ClearanceStatus));
    }

    get releaseJobs(): CustomsClearanceHeader[] {
        return this.clearances.filter(clearance => [
            ClearanceStatus.OUT_FOR_DELIVERY,
            ClearanceStatus.DELIVERED,
            ClearanceStatus.COMPLETED
        ].includes(clearance.status as ClearanceStatus));
    }

    get exceptionJobs(): CustomsClearanceHeader[] {
        return this.clearances
            .filter(clearance => [
                ClearanceStatus.REQUEST_REJECTED,
                ClearanceStatus.DOCS_REVIEW_REJECTED,
                ClearanceStatus.CANCELLED
            ].includes(clearance.status as ClearanceStatus) || this.isPreClearanceLate(clearance))
            .slice(0, 6);
    }

    get recentJobs(): CustomsClearanceHeader[] {
        return this.clearances.slice(0, 8);
    }

    get nextEtaJobs(): CustomsClearanceHeader[] {
        return [...this.clearances]
            .filter(clearance => !!clearance.eta && ![ClearanceStatus.COMPLETED, ClearanceStatus.CANCELLED].includes(clearance.status as ClearanceStatus))
            .sort((a, b) => new Date(a.eta!).getTime() - new Date(b.eta!).getTime())
            .slice(0, 5);
    }

    isPreClearanceLate(clearance: CustomsClearanceHeader): boolean {
        if (!clearance.eta || this.isFinalStatus(clearance.status)) return false;
        const deadline = new Date(clearance.eta).getTime() - 48 * 60 * 60 * 1000;
        return Date.now() > deadline && !this.hasReachedFiling(clearance.status);
    }

    isPreClearanceDueSoon(clearance: CustomsClearanceHeader): boolean {
        if (!clearance.eta || this.isFinalStatus(clearance.status) || this.hasReachedFiling(clearance.status)) return false;
        const deadline = new Date(clearance.eta).getTime() - 48 * 60 * 60 * 1000;
        const now = Date.now();
        return deadline >= now && deadline <= now + 48 * 60 * 60 * 1000;
    }

    preClearanceLabel(clearance: CustomsClearanceHeader): string {
        if (this.isPreClearanceLate(clearance)) return 'Late';
        if (this.isPreClearanceDueSoon(clearance)) return 'Due soon';
        return 'On track';
    }

    getSeverity(status: string | undefined): string {
        switch (`${status || ''}`.toUpperCase()) {
            case 'COMPLETED':
            case 'DELIVERED':
            case 'OUT FOR DELIVERY':
                return 'success';
            case 'CUSTOMS FILING IN PROGRESS':
            case 'CUSTOMS FILING COMPLETED':
            case 'CUSTOMS INSPECTION':
            case 'PORT CHARGES PAID':
            case 'DELIVERY ORDER RECEIVED':
                return 'info';
            case 'DOCS REVIEW REJECTED':
            case 'REQUEST REJECTED':
            case 'CANCELLED':
                return 'danger';
            case 'DOCS REVIEW IN PROGRESS':
            case 'PORT CHARGES PENDING':
                return 'warning';
            default:
                return 'secondary';
        }
    }

    bucketPercent(bucket: StatusBucket): number {
        return Math.round((bucket.value / bucket.total) * 100);
    }

    private countByStatuses(statuses: ClearanceStatus[]): number {
        return this.clearances.filter(clearance => statuses.includes(clearance.status as ClearanceStatus)).length;
    }

    private hasReachedFiling(status: string | undefined): boolean {
        return [
            ClearanceStatus.CUSTOMS_FILING_IN_PROGRESS,
            ClearanceStatus.CUSTOMS_FILING_COMPLETED,
            ClearanceStatus.CUSTOMS_INSPECTION,
            ClearanceStatus.PORT_CHARGES_PENDING,
            ClearanceStatus.PORT_CHARGES_PAID,
            ClearanceStatus.DELIVERY_ORDER_RECEIVED,
            ClearanceStatus.OUT_FOR_DELIVERY,
            ClearanceStatus.DELIVERED,
            ClearanceStatus.COMPLETED
        ].includes(status as ClearanceStatus);
    }

    private isFinalStatus(status: string | undefined): boolean {
        return [ClearanceStatus.COMPLETED, ClearanceStatus.CANCELLED].includes(status as ClearanceStatus);
    }
}
